/**
 * OrgUnitFilter — generischer Org-Baum-Filter (Variante C, Phase 4b).
 *
 * Ersetzt die festen zwei Dropdowns (Niederlassung/Standort) durch EINE
 * Baum-Auswahl, die sich automatisch an die konfigurierte Ebenen-Zahl anpasst.
 * Wählt der Nutzer einen Knoten, gelten alle darunterliegenden Einheiten als
 * ausgewählt (Teilbaum). Die Matching-Helfer bilden Mitarbeiter über
 * `org_unit_id` bzw. `location_id`/`additional_location_ids` auf den Teilbaum ab.
 */
import React from 'react';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Network } from 'lucide-react';

// --- Baum-Helfer -----------------------------------------------------------

/** Flacht den Baum in eine Liste mit Tiefe + Ebenen-Label ab. */
export const flattenOrgTree = (tree = [], levels = [], depth = 0, acc = []) => {
  for (const n of tree) {
    acc.push({
      id: n.id,
      name: n.name,
      level_index: n.level_index,
      levelLabel: levels[n.level_index]?.label || '',
      depth,
    });
    flattenOrgTree(n.children || [], levels, depth + 1, acc);
  }
  return acc;
};

/** Alle Knoten-IDs eines Teilbaums (inkl. Wurzelknoten). */
const collectSubtreeIds = (node, acc = new Set()) => {
  acc.add(node.id);
  for (const c of node.children || []) collectSubtreeIds(c, acc);
  return acc;
};

const findNode = (tree, unitId) => {
  for (const n of tree) {
    if (n.id === unitId) return n;
    const inChild = findNode(n.children || [], unitId);
    if (inChild) return inChild;
  }
  return null;
};

/**
 * Liefert die vom ausgewählten Knoten abgedeckten Mengen:
 *   { unitIds: Set, locationIds: Set }
 * `locationIds` sind die reinen Standort-IDs (ohne `ou-loc-`-Präfix), damit
 * Mitarbeiter über ihr `location_id`/`additional_location_ids` matchen.
 */
export const orgUnitSubtreeSets = (tree = [], unitId) => {
  const empty = { unitIds: new Set(), locationIds: new Set() };
  if (!unitId || unitId === 'all') return null; // null = keine Einschränkung
  const node = findNode(tree, unitId);
  if (!node) return empty;
  const unitIds = collectSubtreeIds(node);
  const locationIds = new Set();
  for (const id of unitIds) {
    if (typeof id === 'string' && id.startsWith('ou-loc-')) {
      locationIds.add(id.slice('ou-loc-'.length));
    }
  }
  return { unitIds, locationIds };
};

/** Prüft, ob ein Mitarbeiter im ausgewählten Teilbaum liegt. */
export const employeeMatchesOrgUnit = (emp, sets) => {
  if (!sets) return true; // 'Alle'
  if (emp.org_unit_id && sets.unitIds.has(emp.org_unit_id)) return true;
  if (emp.location_id && sets.locationIds.has(emp.location_id)) return true;
  const add = emp.additional_location_ids || [];
  return add.some((lid) => sets.locationIds.has(lid));
};

/**
 * Zählt Mitarbeiter je Knoten INKL. aller darunterliegenden Einheiten.
 * Ein Mitarbeiter zählt pro Knoten höchstens einmal (auch bei Zusatz-Standorten).
 * Liefert { unitId: number }.
 */
export const computeOrgUnitCounts = (tree = [], employees = []) => {
  const nodeById = new Map();
  const idxNodes = (nodes) => nodes.forEach((n) => { nodeById.set(n.id, n); idxNodes(n.children || []); });
  idxNodes(tree);

  const counts = {};
  for (const emp of employees) {
    const anchorIds = new Set();
    if (emp.org_unit_id) anchorIds.add(emp.org_unit_id);
    if (emp.location_id) anchorIds.add(`ou-loc-${emp.location_id}`);
    for (const lid of emp.additional_location_ids || []) anchorIds.add(`ou-loc-${lid}`);

    const bumped = new Set();
    for (const aid of anchorIds) {
      const node = nodeById.get(aid);
      if (!node) continue;
      for (const pid of node.path || [aid]) {
        if (!bumped.has(pid)) { bumped.add(pid); counts[pid] = (counts[pid] || 0) + 1; }
      }
    }
  }
  return counts;
};

/**
 * Bildet eine Baum-Auswahl auf die klassischen Backend-Parameter ab
 * (abwärtskompatibel, ohne Backend-Änderung):
 *   - Niederlassungs-Knoten → { branch_id }
 *   - Standort-Knoten       → { location_id }
 *   - tiefere manuelle Einheit → { location_id } des enthaltenden Standorts
 *   - 'all'/nichts          → {}
 */
export const orgUnitToLegacyParams = (tree = [], unitId) => {
  if (!unitId || unitId === 'all') return {};
  const node = findNode(tree, unitId);
  if (!node) return {};
  if (typeof unitId === 'string' && unitId.startsWith('ou-branch-')) {
    return { branch_id: unitId.slice('ou-branch-'.length) };
  }
  if (typeof unitId === 'string' && unitId.startsWith('ou-loc-')) {
    return { location_id: unitId.slice('ou-loc-'.length) };
  }
  // tiefere Einheit: enthaltenden Standort aus dem Pfad ableiten
  const locAncestor = (node.path || []).find((p) => typeof p === 'string' && p.startsWith('ou-loc-'));
  if (locAncestor) return { location_id: locAncestor.slice('ou-loc-'.length) };
  const brAncestor = (node.path || []).find((p) => typeof p === 'string' && p.startsWith('ou-branch-'));
  if (brAncestor) return { branch_id: brAncestor.slice('ou-branch-'.length) };
  return {};
};

// --- Komponente ------------------------------------------------------------

export const OrgUnitFilter = ({
  tree = [], levels = [], value = 'all', onChange,
  label = 'Organisationseinheit', testId = 'filter-org-unit', showLabel = true,
  counts = null, extraOptions = null,
}) => {
  const flat = flattenOrgTree(tree, levels);
  return (
    <div className="space-y-2">
      {showLabel && (
        <Label className="flex items-center gap-1.5">
          <Network className="h-3.5 w-3.5 text-muted-foreground" />{label}
        </Label>
      )}
      <Select value={value || 'all'} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-[320px]">
          <SelectItem value="all">Alle Einheiten</SelectItem>
          {(extraOptions || []).map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
          {flat.map((u) => (
            <SelectItem key={u.id} value={u.id} style={{ paddingLeft: `${12 + u.depth * 14}px` }}>
              {u.name}{u.levelLabel ? ` · ${u.levelLabel}` : ''}
              {counts && counts[u.id] != null ? ` (${counts[u.id]})` : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default OrgUnitFilter;
